import { attr, Constructable, ElementStyles, FASTElement, HostBehavior, HostController, Observable } from '@microsoft/fast-element';
import { designTokens } from '../design-tokens';
import { DI, inject, Registration } from '@microsoft/fast-element/di.js';
import { PropertyStyleSheetBehavior } from '../foundation/property-stylesheet-behavior';

export const mediaSizes = ['extra-small', 'small', 'medium', 'large', 'extra-large'] as const;
export type MediaSize = typeof mediaSizes[number];

const container = DI.getOrCreateDOMContainer();

function getEntryWidth(entry: ResizeObserverEntry): number {
    const borderBoxSize = entry.borderBoxSize as unknown;

    if(Array.isArray(borderBoxSize) && borderBoxSize.length > 0) {
        const size = borderBoxSize[0] as { inlineSize?: number };

        if(typeof size.inlineSize === 'number')
            return size.inlineSize;
    }

    if(borderBoxSize && typeof (borderBoxSize as { inlineSize?: number }).inlineSize === 'number')
        return (borderBoxSize as { inlineSize: number }).inlineSize;

    return entry.contentRect.width;
}

function getMediaSizeForWidth(width: number, element: FASTElement): MediaSize {
    if(width > designTokens['space-breakpoint-extra-large'].getValueFor(element))
        return 'extra-large';
    else if (width > designTokens['space-breakpoint-large'].getValueFor(element))
        return 'large';
    else if (width > designTokens['space-breakpoint-medium'].getValueFor(element))
        return 'medium';
    else if (width > designTokens['space-breakpoint-small'].getValueFor(element))
        return 'small';

    return 'extra-small';
}

function setAttributeIfChanged(target: Element, attributeName: string, value: string) {
    if(target.getAttribute(attributeName) !== value)
        target.setAttribute(attributeName, value);
}

const pendingMediaSizeUpdates = new Map<FASTElement, number>();
let mediaSizeUpdateScheduled = false;

function flushMediaSizeUpdates() {
    mediaSizeUpdateScheduled = false;

    for(const [target, width] of pendingMediaSizeUpdates) {
        setAttributeIfChanged(target, 'media-size', getMediaSizeForWidth(width, target));
    }

    pendingMediaSizeUpdates.clear();
}

container.register(Registration.instance('mediaSizeObserver',
    new ResizeObserver((entries) => {
        for(const entry of entries) {
            const target = entry.target as FASTElement;
            const width = getEntryWidth(entry);

            pendingMediaSizeUpdates.set(target, width);
        }

        if(!mediaSizeUpdateScheduled) {
            mediaSizeUpdateScheduled = true;
            Promise.resolve().then(flushMediaSizeUpdates);
        }
    })
));

container.register(Registration.instance('documentMediaSizeObserver',
    (() =>{
        let targets = new Set<FASTElement>;
        let pendingWidth: number | undefined;
        let updateScheduled = false;

        let flushDocumentMediaSizeUpdate = () => {
            updateScheduled = false;

            if(pendingWidth === undefined)
                return;

            const width = pendingWidth;
            pendingWidth = undefined;

            for(const target of targets) {
                setAttributeIfChanged(target, 'document-media-size', getMediaSizeForWidth(width, target));
            }
        }

        let documentResizeObserver = new ResizeObserver((entries) => {
            var width = document.body.clientWidth;

            pendingWidth = width;

            if(!updateScheduled) {
                updateScheduled = true;
                Promise.resolve().then(flushDocumentMediaSizeUpdate);
            }
        })

        documentResizeObserver.observe(document.body);

        return {
            register: (target: FASTElement) => {
                targets.add(target);
                setAttributeIfChanged(target, 'document-media-size', getMediaSizeForWidth(document.body.clientWidth, target));
            },
            unregister: (target: FASTElement) => targets.delete(target)
        }
    })()
));

export function mediaSizeBehavior(value: string, styles: ElementStyles, negate: boolean = false) {
    return new PropertyStyleSheetBehavior('mediaSize', value, styles, negate);
}

export function documentMediaSizeBehavior(value: string, styles: ElementStyles, negate: boolean = false) {
    return new PropertyStyleSheetBehavior('documentMediaSize', value, styles, negate);
}

export function MediaSizeMixin<TBase extends Constructable<FASTElement>>(Base: TBase) {
    class mediaSizeMixin extends Base {
        @inject('mediaSizeObserver')
        protected mediaSizeObserver!: ResizeObserver;

        /** A readonly attribute which reflects the closest breakpoint size of this container. */
        @attr({ attribute: 'media-size' })
        mediaSize: MediaSize;

        connectedCallback() {
            super.connectedCallback();
            this.mediaSizeObserver.observe(this);
        }

        disconnectedCallback() {
            super.disconnectedCallback();
            this.mediaSizeObserver.unobserve(this);
        }
    }

    return mediaSizeMixin;
}

export function DocumentMediaSizeMixin<TBase extends Constructable<FASTElement>>(Base: TBase) {
    class documentMediaSizeMixin extends Base {
        @inject('documentMediaSizeObserver')
        protected documentMediaSizeObserver!: any;

        /** A readonly attribute which reflects the closest breakpoint size of this container. */
        @attr({ attribute: 'document-media-size' })
        documentMediaSize: MediaSize;

        connectedCallback() {
            super.connectedCallback();
            this.documentMediaSizeObserver.register(this);
        }

        disconnectedCallback() {
            super.disconnectedCallback();
            this.documentMediaSizeObserver.unregister(this);
        }
    }

    return documentMediaSizeMixin;
}

export class RelativeMediaSizeStyleSheetBehavior implements HostBehavior {
    constructor(
        private propertyName: string,
        private value: MediaSize,
        private styles: ElementStyles,
        private negate: boolean = false,
    ) {}

    public addedCallback(controller: HostController) {
        Observable.getNotifier(controller.source).subscribe(this, this.propertyName);
        this.handleChange(controller.source, this.propertyName);
    }

    public removedCallback(controller: HostController) {
        Observable.getNotifier(controller.source).unsubscribe(this, this.propertyName);
        controller.removeStyles(this.styles);
    }

    public handleChange(source: FASTElement, key: string) {
        const controller = source.$fastController;

        if ((mediaSizes.indexOf(source[key]) >= mediaSizes.indexOf(this.value)) !== this.negate)
            controller.addStyles(this.styles);
        else
            controller.removeStyles(this.styles);
    }
}

export function relativeMediaSizeBehavior(property: string, value: MediaSize, styles: ElementStyles, negate: boolean = false) {
    return new RelativeMediaSizeStyleSheetBehavior(property, value, styles);
}
